import { shuffle } from './rng.js';
export const COLOR_SUITS = ['pink', 'green', 'blue', 'yellow'];
export const COLOR_MAX_RANK = 9;
export const ROCKET_MAX_RANK = 4;
export const DECK_SIZE = COLOR_SUITS.length * COLOR_MAX_RANK + ROCKET_MAX_RANK; // 40
export function cardId(card) {
    return `${card.suit}${card.rank}`;
}
const CARD_ID_RE = /^(pink|green|blue|yellow|rocket)([1-9])$/;
export function parseCardId(id) {
    const m = CARD_ID_RE.exec(id);
    if (!m)
        throw new Error(`Not a card id: ${id}`);
    const suit = m[1];
    const rank = Number(m[2]);
    const max = suit === 'rocket' ? ROCKET_MAX_RANK : COLOR_MAX_RANK;
    if (rank < 1 || rank > max)
        throw new Error(`Rank out of range for ${suit}: ${rank}`);
    return { suit, rank };
}
export function tryParseCardId(id) {
    try {
        return parseCardId(id);
    }
    catch {
        return null;
    }
}
export function isRocket(card) {
    return card.suit === 'rocket';
}
export function sameCard(a, b) {
    return a.suit === b.suit && a.rank === b.rank;
}
export function buildDeck() {
    const deck = [];
    for (const suit of COLOR_SUITS) {
        for (let rank = 1; rank <= COLOR_MAX_RANK; rank++)
            deck.push({ suit, rank });
    }
    for (let rank = 1; rank <= ROCKET_MAX_RANK; rank++)
        deck.push({ suit: 'rocket', rank });
    return deck;
}
/** Only the 36 colour cards can become task cards - rockets never do. */
export function taskEligibleCards() {
    return buildDeck().filter((c) => !isRocket(c));
}
const SUIT_ORDER = { pink: 0, green: 1, blue: 2, yellow: 3, rocket: 4 };
/** Stable display order: rockets last, ascending rank within a suit. */
export function sortHand(cards) {
    return cards
        .slice()
        .sort((a, b) => SUIT_ORDER[a.suit] - SUIT_ORDER[b.suit] || a.rank - b.rank);
}
/**
 * Deal the whole deck round-robin. With 3 players one seat gets the extra card,
 * which is why `startSeat` matters - the caller rotates it between attempts.
 */
export function dealHands(playerCount, rng, startSeat = 0) {
    if (playerCount < 3 || playerCount > 7) {
        throw new Error(`Unsupported player count: ${playerCount}`);
    }
    const deck = shuffle(buildDeck(), rng);
    const hands = Array.from({ length: playerCount }, () => []);
    deck.forEach((card, i) => {
        const seat = (startSeat + i) % playerCount;
        hands[seat].push(card);
    });
    return hands.map(sortHand);
}
/** Index into `plays` of the winning card. Rockets trump; otherwise led suit wins. */
export function trickWinnerIndex(plays) {
    if (plays.length === 0)
        throw new Error('Empty trick has no winner');
    const led = plays[0].suit;
    const hasRocket = plays.some(isRocket);
    const relevant = hasRocket ? 'rocket' : led;
    let best = -1;
    let bestRank = -1;
    plays.forEach((card, i) => {
        if (card.suit !== relevant)
            return;
        if (card.rank > bestRank) {
            bestRank = card.rank;
            best = i;
        }
    });
    return best;
}
/** Follow-suit rule: you must match the led suit while you still hold it. */
export function legalPlays(hand, ledSuit) {
    if (ledSuit === null)
        return hand.slice();
    const matching = hand.filter((c) => c.suit === ledSuit);
    return matching.length > 0 ? matching : hand.slice();
}
export function findCommanderSeat(hands) {
    const seat = hands.findIndex((hand) => hand.some((c) => c.suit === 'rocket' && c.rank === ROCKET_MAX_RANK));
    if (seat < 0)
        throw new Error('No player holds the highest rocket');
    return seat;
}
//# sourceMappingURL=cards.js.map